import type { App6EchelonCode, OdbSide } from "../../api/types";

/** Code SIDC APP-6D d'une unité construite, calculé dans le navigateur pour un affichage immédiat (ligne de
 *  l'arbre, palette, aperçus) : même gabarit que `symbology.sidc_parts` côté serveur, qui reste la référence (le
 *  serveur le recalcule à chaque aperçu). Vecteurs partagés : fixtures/sidc_vectors.json.
 *
 *      VV C I SS S H EE EEEEEE M1 M2
 *      10 0 3 10 0 4 15 121104 00 00
 *  version 10, contexte 0, identité (3 ami, 6 ennemi), ensemble (10 terrestre, 11 organisation civile), statut 0,
 *  QG / force opérationnelle (0, 2, 4, 6), échelon (amplificateur B), entité, modificateurs secteurs 1 et 2. */

/** Fonction APP-6 stockée : « SS-EEEEEE-M1-M2 ». */
export const APP6_RE = /^(10|11)-\d{6}-\d{2}-\d{2}$/;

export interface App6Parts {
  set: string;
  entity: string;
  m1: string;
  m2: string;
}

export function parseApp6(code: string | null | undefined): App6Parts | null {
  if (!code || !APP6_RE.test(code)) return null;
  const [set = "", entity = "", m1 = "", m2 = ""] = code.split("-");
  return { set, entity, m1, m2 };
}

export function formatApp6(parts: App6Parts): string {
  return `${parts.set}-${parts.entity}-${parts.m1}-${parts.m2}`;
}

/** Même fonction, autre qualificatif (modificateur secteur 2) ; « 00 » le retire. */
export function withM2(code: string, m2: string): string {
  const parts = parseApp6(code);
  return parts ? formatApp6({ ...parts, m2 }) : code;
}

/** Code court affiché (« 121104 », « 130300·41 », « 121100 / 01 ») : l'entité, et ses modificateurs s'il y en a. */
export function shortApp6(code: string | null | undefined): string {
  const parts = parseApp6(code);
  if (!parts) return "";
  const m1 = parts.m1 !== "00" ? `·${parts.m1}` : "";
  const m2 = parts.m2 !== "00" ? ` / ${parts.m2}` : "";
  return `${parts.entity}${m1}${m2}`;
}

export interface SidcInput {
  side: OdbSide;
  app6: string | null | undefined;
  echelon: App6EchelonCode | null | undefined;
  hq?: boolean | null;
  tf?: boolean | null;
}

const HQ_TF: Record<string, string> = { "false|false": "0", "true|false": "2", "false|true": "4", "true|true": "6" };

/** SIDC à 20 chiffres, ou null si la fonction est illisible. L'amplificateur d'échelon vaut « 00 » pour une
 *  organisation civile (ensemble 11) et pour une unité sans échelon. */
export function sidcOf({ side, app6, echelon, hq, tf }: SidcInput): string | null {
  const parts = parseApp6(app6);
  if (!parts) return null;
  const identity = side === "ENI" ? "6" : "3";
  const amplifier = parts.set === "11" || !echelon || echelon === "00" ? "00" : echelon;
  return `100${identity}${parts.set}0${HQ_TF[`${Boolean(hq)}|${Boolean(tf)}`]}${amplifier}${parts.entity}${parts.m1}${parts.m2}`;
}

const SIDC_RE = /^\d{20}$/;

/** Échelon APP-6 porté par un SIDC (chiffres 9 et 10), ou null (« 00 » compris : aucun échelon dessiné). */
export function echelonFromSidc(sidc: string | null | undefined): App6EchelonCode | null {
  if (!sidc || !SIDC_RE.test(sidc)) return null;
  const code = sidc.slice(8, 10);
  return (["11", "12", "13", "14", "15", "16", "17", "18", "21"] as const).find((c) => c === code) ?? null;
}

/** QG et force opérationnelle portés par un SIDC (chiffre 8 : 2 ou 6 QG, 4 ou 6 force opérationnelle). */
export function hqTfFromSidc(sidc: string | null | undefined): { hq: boolean; tf: boolean } {
  const digit = sidc && SIDC_RE.test(sidc) ? sidc[7] : "0";
  return { hq: digit === "2" || digit === "6", tf: digit === "4" || digit === "6" };
}

/** Fonction APP-6 (« SS-EEEEEE-M1-M2 ») portée par un SIDC. */
export function app6FromSidc(sidc: string | null | undefined): string | null {
  if (!sidc || !SIDC_RE.test(sidc)) return null;
  return `${sidc.slice(4, 6)}-${sidc.slice(10, 16)}-${sidc.slice(16, 18)}-${sidc.slice(18, 20)}`;
}
